import Lenis from 'lenis'
import {onBeforeUnmount, onMounted} from 'vue'

let lenis: Lenis | null = null

export interface WwwScrollOptions {
    immediate?: boolean
    offset?: number
}

/** Scroll the www marketing site — uses Lenis when active, native scroll otherwise. */
export function wwwScrollTo(
    target: number | string | HTMLElement,
    options: WwwScrollOptions = {},
): void {
    const {immediate = false, offset = 0} = options

    if (lenis) {
        lenis.scrollTo(target, {immediate, offset})
        return
    }

    if (typeof target === 'number') {
        window.scrollTo({top: target, behavior: immediate ? 'auto' : 'smooth'})
        return
    }

    const el = typeof target === 'string' ? document.querySelector(target) : target
    if (!el) return

    const top = el.getBoundingClientRect().top + window.scrollY + offset
    window.scrollTo({top, behavior: immediate ? 'auto' : 'smooth'})
}

/**
 * Lenis smooth scroll with a single rAF loop (ported from osello.com).
 *
 * Respects `prefers-reduced-motion: reduce` — leaves native scrolling
 * untouched when set, and tears down if the preference flips mid-session.
 * Call once from the www root component.
 */
export function useLenis(): void {
    let frame = 0
    let query: MediaQueryList | null = null

    function start(): void {
        if (lenis) return
        lenis = new Lenis({
            duration: 1.1,
            // Soft exponential ease-out — quick to settle, no rubber-band.
            easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
            smoothWheel: true,
        })
        const raf = (time: number) => {
            lenis?.raf(time)
            frame = requestAnimationFrame(raf)
        }
        frame = requestAnimationFrame(raf)
    }

    function stop(): void {
        cancelAnimationFrame(frame)
        lenis?.destroy()
        lenis = null
    }

    function onPreferenceChange(event: MediaQueryListEvent): void {
        if (event.matches) stop()
        else start()
    }

    onMounted(() => {
        query = window.matchMedia('(prefers-reduced-motion: reduce)')
        if (!query.matches) start()
        query.addEventListener('change', onPreferenceChange)
    })

    onBeforeUnmount(() => {
        query?.removeEventListener('change', onPreferenceChange)
        stop()
    })
}

export default useLenis
